/* A planned change to an agent's tables, step by step: what each change does, what it does
   to the rows, and why the rows block it. In `shared/` because the Spaces tab shows it
   before a change is made, and the chat's approval card shows Definable's planned changes
   with it (rule book §1 rule 5: features never import each other). */
import React from "react";
import { CircleAlert, Loader2 } from "lucide-react";

import { cn } from "@/shared/lib/cn";

import type { SpacePlan } from "@/shared/types";

export default function SpacePlanSteps({ plan, pending, error, className }: {
  plan?: SpacePlan;
  pending?: boolean;
  /** Why the changes can't be planned: they name what isn't there. */
  error?: string | null;
  className?: string;
}) {
  if (pending && !plan) {
    return (
      <div className={cn("flex items-center gap-2 px-3 py-3 text-[12.5px] text-[var(--text-tertiary)]", className)}>
        <Loader2 className="w-3.5 h-3.5 animate-spin" strokeWidth={1.75} /> Checking the rows…
      </div>
    );
  }
  if (error) {
    return (
      <div role="alert" className={cn("flex gap-2 px-3 py-3 text-[12.5px] leading-[1.5] text-[var(--danger)]", className)}>
        <CircleAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={1.75} /> {error}
      </div>
    );
  }
  if (!plan) return null;
  // the last plan stays, dimmed, while the next is checked: the panel never jumps as one types
  return (
    <ol aria-busy={pending || undefined} className={cn("m-0 p-0 list-none divide-y divide-[var(--hairline)] transition-opacity", pending && "opacity-60", className)}>
      {plan.steps.map((step, index) => (
        <li key={`${index}:${step.says}`} className="flex gap-3 px-3 py-2.5 min-w-0">
          <span className="w-5 h-5 shrink-0 grid place-items-center rounded-[6px] bg-[var(--surface-2)] text-[11px] font-mono text-[var(--text-secondary)]">
            {index + 1}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-[13px] font-medium text-[var(--text-primary)] break-words">{step.says}</span>
            {step.effect && (
              <span className="block mt-0.5 text-[12px] text-[var(--text-tertiary)] break-words">{step.effect}</span>
            )}
            {step.blocked && (
              <span className="flex items-start gap-1.5 mt-1 text-[12px] text-[var(--danger)]">
                <CircleAlert className="w-3.5 h-3.5 mt-[1px] shrink-0" strokeWidth={1.75} /> {step.blocked}
              </span>
            )}
          </span>
        </li>
      ))}
    </ol>
  );
}
